// src/services/ApiFlowEngine.js
import ApiClient from './ApiClient';

class ApiFlowEngine {
  constructor(flowConfig, options = {}) {
    this.flowConfig = flowConfig;
    this.executionStatus = flowConfig.executionStatus;
    this.timeout = options.timeout || 30000; // Default timeout
    this.abortController = flowConfig.abortController || new AbortController();
    this.callbacks = options.callbacks || {};
    this.haltOnFunctionError = options.haltOnFunctionError !== undefined ? options.haltOnFunctionError : false;
    
    // Create an API client with the flow credentials and callbacks
    this.apiClient = new ApiClient(flowConfig.credentials, { 
      timeout: this.timeout,
      callbacks: {
        // Pass through callbacks with additional context
        beforeRequest: (data) => this.notifyCallback('beforeRequest', {
          ...data,
          moduleId: this.executionStatus.currentNodeId,
          endpointKey: this.executionStatus.currentEndpointIndex
        }),
        afterResponse: (data) => this.notifyCallback('afterResponse', {
          ...data,
          moduleId: this.executionStatus.currentNodeId,
          endpointKey: this.executionStatus.currentEndpointIndex
        }),
        onError: (data) => this.notifyCallback('onError', {
          ...data,
          moduleId: this.executionStatus.currentNodeId,
          endpointKey: this.executionStatus.currentEndpointIndex
        })
      }
    });
  }

  // Main execution method - now handles both API and function modules
  async execute(startIndex = 0) {
    this.executionStatus.isExecuting = true;
    
    for (let i = startIndex; i < this.flowConfig.executionOrder.length; i++) {
      if (this.abortController.signal.aborted) break;
      
      const moduleId = this.flowConfig.executionOrder[i];
      const moduleNode = this.flowConfig.moduleNodes[moduleId];
      
      let success = true;
      
      // Check module type and execute accordingly
      if (moduleNode.moduleType === 'function') {
        success = await this.executeFunctionModule(moduleId, moduleNode);
      } else {
        // Default to API module if not specified or type is 'api'
        success = await this.executeApiModule(moduleId, moduleNode);
      }
      
      // Stop the workflow if a module failed and we need to halt
      if (!success) break;
      
      // Add delay between modules if not the last one
      if (i < this.flowConfig.executionOrder.length - 1) {
        await new Promise(resolve => setTimeout(resolve, this.flowConfig.apiInterval));
      }
    }
    
    // Only update completion status if not aborted
    if (!this.abortController.signal.aborted) {
      this.executionStatus.isExecuting = false;
      this.executionStatus.currentNodeId = null;
      this.executionStatus.currentEndpointIndex = null;
      
      this.notifyCallback('onComplete', { success: true });
    }
    
    return !this.abortController.signal.aborted;
  }
  
  // New method to handle function module execution
  async executeFunctionModule(moduleId, moduleNode) {
    this.executionStatus.currentNodeId = moduleId;
    
    const functionDef = moduleNode.function;
    const functionKey = functionDef.key || 'function';
    
    this.executionStatus.currentEndpointIndex = functionKey;
    
    // Notify before execution
    this.notifyCallback('beforeRequest', {
      moduleId,
      endpointKey: functionKey,
      functionName: functionDef.name,
      functionParams: functionDef.params
    });
    
    try {
      // Track execution time
      const startTime = new Date();
      
      // Execute the function with its parameters
      const result = await functionDef.handler(...(functionDef.params || []));
      
      // Store response in the same format as API responses
      if (!this.executionStatus.responses[moduleId]) {
        this.executionStatus.responses[moduleId] = {};
      }
      
      this.executionStatus.responses[moduleId][functionKey] = {
        data: {
          result,
          functionName: functionDef.name
        },
        timestamp: new Date(),
        duration: new Date() - startTime,
        status: 'success'
      };
      
      // Notify after execution
      this.notifyCallback('afterResponse', {
        moduleId,
        endpointKey: functionKey,
        response: {
          result,
          functionName: functionDef.name
        }
      });
      
      return true;
      
    } catch (error) {
      // Handle function errors
      if (!this.executionStatus.errors[moduleId]) {
        this.executionStatus.errors[moduleId] = {};
      }
      
      this.executionStatus.errors[moduleId][functionKey] = {
        message: error.message,
        timestamp: new Date(),
        code: 'FUNCTION_ERROR',
        status: 'error'
      };
      
      // Notify of error
      this.notifyCallback('onError', {
        moduleId,
        endpointKey: functionKey,
        error: {
          message: error.message,
          code: 'FUNCTION_ERROR'
        }
      });
      
      // Return based on error handling configuration
      return !this.haltOnFunctionError;
    }
  }
  
  // Existing API module execution logic moved to its own method
  async executeApiModule(moduleId, moduleNode) {
    // Process each endpoint in the API module
    const endpointKeys = Object.keys(moduleNode)
      .filter(key => key.startsWith('endpoint_'))
      .sort(); // Ensure consistent order
    
    for (let i = 0; i < endpointKeys.length; i++) {
      const endpointKey = endpointKeys[i];
      
      if (this.abortController.signal.aborted) break;
      
      this.executionStatus.currentNodeId = moduleId;
      this.executionStatus.currentEndpointIndex = endpointKey;
      
      try {
        // Track call submission time
        const startTime = new Date();
        
        // Execute API call using the API client
        const response = await this.apiClient.executeApiCall(moduleNode[endpointKey]);
        
        // Store response
        if (!this.executionStatus.responses[moduleId]) {
          this.executionStatus.responses[moduleId] = {};
        }
        
        this.executionStatus.responses[moduleId][endpointKey] = {
          data: response,
          timestamp: new Date(),
          duration: new Date() - startTime,
          status: 'success'
        };
        
        // Add delay between endpoints if this isn't the last one
        if (i < endpointKeys.length - 1) {
          await new Promise(resolve => setTimeout(resolve, this.flowConfig.apiInterval));
        }
        
      } catch (error) {
        // Handle errors
        if (!this.executionStatus.errors[moduleId]) {
          this.executionStatus.errors[moduleId] = {};
        }
        
        this.executionStatus.errors[moduleId][endpointKey] = {
          message: error.message,
          timestamp: new Date(),
          code: error.code || 'UNKNOWN_ERROR',
          status: 'error'
        };
        
        // Abort execution on API error (maintaining original behavior)
        return false;
      }
    }
    
    return true;
  }
  
  // Method to interrupt execution - unchanged
  interrupt() {
    this.abortController.abort();
    this.apiClient.interrupt();
    this.executionStatus.isExecuting = false;
    this.notifyCallback('onInterrupt', {
      lastModuleId: this.executionStatus.currentNodeId,
      lastEndpointIndex: this.executionStatus.currentEndpointIndex
    });
  }
  
  // Helper method for callbacks - unchanged
  notifyCallback(event, data) {
    if (this.callbacks[event]) {
      this.callbacks[event](data);
    }
  }
}

export default ApiFlowEngine;





// import ApiClient from './ApiClient';

// class ApiFlowEngine {
//   constructor(flowConfig, options = {}) {
//     this.flowConfig = flowConfig;
//     this.executionStatus = flowConfig.executionStatus;
//     this.timeout = options.timeout || 30000; // Default timeout
//     this.abortController = new AbortController();
//     this.callbacks = options.callbacks || {};
    
//     // Create an API client with the flow credentials and callbacks
//     this.apiClient = new ApiClient(flowConfig.credentials, { 
//       timeout: this.timeout,
//       callbacks: {
//         // Pass through callbacks with additional context
//         beforeRequest: (data) => this.notifyCallback('beforeRequest', {
//           ...data,
//           moduleId: this.executionStatus.currentNodeId,
//           endpointKey: this.executionStatus.currentEndpointIndex
//         }),
//         afterResponse: (data) => this.notifyCallback('afterResponse', {
//           ...data,
//           moduleId: this.executionStatus.currentNodeId,
//           endpointKey: this.executionStatus.currentEndpointIndex
//         }),
//         onError: (data) => this.notifyCallback('onError', {
//           ...data,
//           moduleId: this.executionStatus.currentNodeId,
//           endpointKey: this.executionStatus.currentEndpointIndex
//         })
//       }
//     });
//   }

//   // Main execution method
//   async execute(startIndex = 0) {
//     this.executionStatus.isExecuting = true;
    
//     for (let i = startIndex; i < this.flowConfig.executionOrder.length; i++) {
//       if (this.abortController.signal.aborted) break;
      
//       const moduleId = this.flowConfig.executionOrder[i];
//       const moduleNode = this.flowConfig.moduleNodes[moduleId];
      
//       // Process each endpoint in the module
//       const endpointKeys = Object.keys(moduleNode).filter(key => key.startsWith('endpoint_'));
      
//       for (const endpointKey of endpointKeys) {
//         if (this.abortController.signal.aborted) break;
        
//         this.executionStatus.currentNodeId = moduleId;
//         this.executionStatus.currentEndpointIndex = endpointKey;
        
//         try {
//           // Track call submission time
//           const startTime = new Date();
          
//           // Execute API call using the API client
//           const response = await this.apiClient.executeApiCall(moduleNode[endpointKey]);
          
//           // Store response
//           if (!this.executionStatus.responses[moduleId]) {
//             this.executionStatus.responses[moduleId] = {};
//           }
//           this.executionStatus.responses[moduleId][endpointKey] = {
//             data: response,
//             timestamp: new Date(),
//             duration: new Date() - startTime,
//             status: 'success'  // Add the status field back in
//           };
//           console.log(`Response stored in engine for ${moduleId}.${endpointKey}:`, 
//             this.executionStatus.responses[moduleId][endpointKey]);
          
//           // Wait for the configured interval before next call
//           await new Promise(resolve => setTimeout(resolve, this.flowConfig.apiInterval));
          
//         } catch (error) {
//           // Handle errors
//           if (!this.executionStatus.errors[moduleId]) {
//             this.executionStatus.errors[moduleId] = {};
//           }
          
//           this.executionStatus.errors[moduleId][endpointKey] = {
//             message: error.message,
//             timestamp: new Date(),
//             code: error.code || 'UNKNOWN_ERROR',
//             status: 'error'  // Add status field for errors as well
//           };
          
//           // Abort execution on error
//           return false;
//         }
//       }
//     }
    
//     this.executionStatus.isExecuting = false;
//     this.executionStatus.currentNodeId = null;
//     this.executionStatus.currentEndpointIndex = null;
    
//     this.notifyCallback('onComplete', { success: true });
//     return true;
//   }
  
//   // Method to interrupt execution
//   interrupt() {
//     this.abortController.abort();
//     this.apiClient.interrupt();
//     this.executionStatus.isExecuting = false;
//     this.notifyCallback('onInterrupt', {
//       lastModuleId: this.executionStatus.currentNodeId,
//       lastEndpointIndex: this.executionStatus.currentEndpointIndex
//     });
//   }
  
//   // Helper method for callbacks
//   notifyCallback(event, data) {
//     if (this.callbacks[event]) {
//       this.callbacks[event](data);
//     }
//   }
// }

// // Export the class
// export default ApiFlowEngine;